// What is wrong with a work centre on one flowline, in one word.
//
// The leader's tiles, rows and cards all carry the same word and colour, and
// they used to disagree: one screen called any `delta < 0` red, so by
// mid-morning most of the floor was red and the word meant nothing. This is the
// one place the word is decided, worst thing first, so a station that is both
// stopped and behind reads as stopped — the thing a leader can do something
// about — rather than as a number.
//
// Odoo's own reconciler answers the questions that are about the pair itself:
// `downtime`, `off_shift`, `no_work`, `starved` are its words and are taken as
// read. Pace is this app's — Odoo has no "behind" — and the thresholds are the
// mock's: two pods or a fifth of the day's number is red, one pod is amber,
// because one pod is inside the time for a pod and two is not.

import type { StationStateVM } from '@/api/types';

export type ToneKind = 'danger' | 'warn' | 'pos' | 'grey';

export type StationWord =
  | 'stopped'
  | 'offShift'
  | 'noTarget'
  | 'noWork'
  | 'queueEmpty'
  | 'done'
  | 'starved'
  | 'behind'
  | 'onPace';

export interface StationTone {
  kind: ToneKind;
  word: StationWord;
  /** Pods behind pace, for the "Behind 3" word. Zero otherwise. */
  behind: number;
  /** done − dueNow, when a pace exists. */
  delta: number | null;
  /** The andon that stopped it, when one has. */
  stopAlert: { id: number; name: string } | null;
}

/** The ranking a leader reads in: what needs them first. */
export const TONE_RANK: Record<ToneKind, number> = { danger: 0, warn: 1, pos: 2, grey: 3 };

/**
 * @param done Completions at the pair today.
 * @param target Today's number for the pair, or null when nobody set one.
 * @param dueNow Pro-rata target by now on the station's own calendar, or null
 *   off shift / without a target.
 */
export function stationTone(
  st: Pick<StationStateVM, 'status' | 'onShift' | 'waiting' | 'ready' | 'running' | 'stopAlert'>,
  done: number,
  target: number | null,
  dueNow: number | null,
): StationTone {
  const base = { behind: 0, delta: null as number | null, stopAlert: st.stopAlert };

  // A stopped station is stopped whatever its numbers say. Odoo marks the pair
  // `downtime` while the andon that stopped it is open; the alert itself is
  // carried so the tile can say which one.
  if (st.status === 'downtime' || st.stopAlert) return { ...base, kind: 'danger', word: 'stopped' };
  if (st.status === 'off_shift' || !st.onShift) return { ...base, kind: 'grey', word: 'offShift' };
  if (st.status === 'no_work' || (st.waiting === 0 && done === 0)) return { ...base, kind: 'grey', word: 'noWork' };
  // No target is not "on pace" — there is nothing to be on. Said plainly rather
  // than borrowing the off-shift word for it, which is what the mock did.
  if (target == null || target <= 0) return { ...base, kind: 'grey', word: 'noTarget' };

  const delta = dueNow == null ? null : done - dueNow;
  const behind = delta != null && delta < 0 ? -delta : 0;

  // Nothing left to do here today. Whether that is good depends on whether the
  // day's number is met: a queue that ran dry with pods still owed is a
  // supplier problem, not a finished shift.
  if (st.waiting === 0) {
    return behind > 0
      ? { ...base, kind: 'warn', word: 'queueEmpty', behind, delta }
      : { ...base, kind: 'pos', word: 'done', delta };
  }
  if (st.status === 'starved' || (st.ready === 0 && st.running === 0 && done < target)) {
    return { ...base, kind: 'warn', word: 'starved', behind, delta };
  }
  if (behind >= 2 || behind >= target * 0.2) return { ...base, kind: 'danger', word: 'behind', behind, delta };
  if (behind >= 1) return { ...base, kind: 'warn', word: 'behind', behind, delta };
  return { ...base, kind: 'pos', word: 'onPace', delta };
}

/** The worst of several pairs — the word a work centre carries across its lines. */
export function worstTone(tones: StationTone[]): StationTone | null {
  return [...tones].sort((a, b) => TONE_RANK[a.kind] - TONE_RANK[b.kind] || b.behind - a.behind)[0] ?? null;
}
